import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Check, Minus, Zap, Crown, Building2 } from 'lucide-react';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$15',
    period: '/mo',
    desc: 'For small teams',
    icon: Zap,
    features: [
      { text: '15 employees', highlight: false },
      { text: '15 PAF files', highlight: false },
      { text: 'Email reminders', included: true },
      { text: 'PDF packet generation', included: true },
      { text: 'Priority support', included: false },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$25',
    period: '/mo',
    desc: 'Best for growing teams',
    icon: Crown,
    recommended: true,
    features: [
      { text: '25 employees', highlight: true },
      { text: '25 PAF files', highlight: true },
      { text: 'Email reminders', included: true },
      { text: 'PDF packet generation', included: true },
      { text: 'Priority support', included: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For large organizations',
    icon: Building2,
    features: [
      { text: 'Unlimited employees', highlight: true },
      { text: 'Unlimited PAFs', highlight: true },
      { text: 'Email reminders', included: true },
      { text: 'PDF packet generation', included: true },
      { text: 'Dedicated support + SLA', included: true },
    ],
  },
];

export function UpgradeModal({ open, onClose, limitType = 'employee', currentPlan = 'starter', usage = {} }) {
  const { apiCall } = useAuth();
  const [upgrading, setUpgrading] = useState('');

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleUpgrade = async (planId) => {
    if (planId === 'enterprise') {
      window.location.href = '/contact';
      return;
    }
    setUpgrading(planId);
    try {
      const res = await apiCall('post', '/payments/create-checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
    } finally {
      setUpgrading(false);
    }
  };

  const limitLabel = limitType === 'employee'
    ? `Employee limit reached (${usage.employees_used || 0}/${usage.employees_limit || 0})`
    : `PAF limit reached (${usage.pafs_used || 0}/${usage.pafs_limit || 0})`;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="upgrade-modal">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-[840px] bg-white rounded-3xl shadow-2xl shadow-indigo-200/30 overflow-hidden mx-4"
          >
            {/* Close */}
            <button
              onClick={onClose}
              data-testid="upgrade-modal-close"
              className="absolute top-4 right-4 w-8 h-8 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-colors z-10"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {/* Header */}
            <div className="text-center pt-7 pb-5 border-b border-slate-100 px-8">
              <div className="inline-flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 text-xs font-bold px-3.5 py-1.5 rounded-full mb-3">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
                {limitLabel}
              </div>
              <h2 className="text-xl font-extrabold text-[#0F172A] mb-1">Upgrade Your Plan</h2>
              <p className="text-sm text-[#64748B]">Unlock more capacity for your growing team</p>
            </div>

            {/* Plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-6" data-testid="upgrade-plans-grid">
              {PLANS.map((plan) => {
                const isCurrent = plan.id === currentPlan;
                const isRecommended = plan.recommended && !isCurrent;
                const PlanIcon = plan.icon;

                return (
                  <div
                    key={plan.id}
                    data-testid={`plan-card-${plan.id}`}
                    className={`relative rounded-2xl p-5 border-2 transition-all ${
                      isCurrent
                        ? 'border-slate-200 bg-slate-50/60 opacity-70'
                        : isRecommended
                        ? 'border-indigo-500 bg-gradient-to-b from-indigo-50/30 to-white shadow-lg shadow-indigo-100/40'
                        : 'border-slate-200 bg-white hover:border-indigo-200 hover:shadow-md'
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white text-[10px] font-bold tracking-wider px-3.5 py-1 rounded-full">
                        RECOMMENDED
                      </div>
                    )}

                    <div className="flex items-center gap-2 mb-1">
                      <PlanIcon className={`w-4 h-4 ${isCurrent ? 'text-slate-400' : 'text-indigo-500'}`} strokeWidth={1.5} />
                      <span className="text-sm font-bold text-[#0F172A]">{plan.name}</span>
                    </div>
                    <div className="flex items-baseline gap-0.5 mb-0.5">
                      <span className="text-3xl font-extrabold text-[#0F172A]">{plan.price}</span>
                      {plan.period && <span className="text-sm text-[#94A3B8]">{plan.period}</span>}
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">{isCurrent ? 'Your current plan' : plan.desc}</p>

                    <ul className="space-y-2.5 mb-5">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-[13px]">
                          <div className={`w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 ${
                            f.included === false ? 'bg-slate-100' : 'bg-emerald-50'
                          }`}>
                            {f.included === false
                              ? <Minus className="w-3 h-3 text-slate-400" strokeWidth={2} />
                              : <Check className="w-3 h-3 text-emerald-500" strokeWidth={3} />
                            }
                          </div>
                          <span className={
                            f.included === false ? 'text-[#94A3B8]'
                            : f.highlight ? 'font-bold text-indigo-600'
                            : 'text-[#0F172A]'
                          }>{f.text}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrent ? (
                      <button
                        disabled
                        className="w-full py-2.5 rounded-xl text-sm font-bold bg-slate-100 text-slate-400 cursor-default"
                        data-testid={`plan-btn-${plan.id}`}
                      >
                        Current Plan
                      </button>
                    ) : plan.id === 'enterprise' ? (
                      <button
                        onClick={() => handleUpgrade('enterprise')}
                        className="w-full py-2.5 rounded-xl text-sm font-bold border-2 border-slate-200 text-indigo-600 bg-white hover:border-indigo-500 hover:bg-indigo-50/50 transition-all"
                        data-testid={`plan-btn-${plan.id}`}
                      >
                        Contact Sales
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={upgrading === plan.id}
                        className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-indigo-500 shadow-md shadow-indigo-500/25 hover:shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5 transition-all disabled:opacity-60"
                        data-testid={`plan-btn-${plan.id}`}
                      >
                        {upgrading === plan.id ? 'Redirecting...' : `Upgrade to ${plan.name}`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="text-center pb-6 px-8">
              <p className="text-xs text-[#94A3B8]">
                All plans include compliance automation, audit trails, and team collaboration.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
